/**
 * SMS Help content (Issue #2212, extracted from SmsHelpPage.tsx / #1320)
 *
 * Single source for web SmsHelpPage and the native SMS Help screen.
 * 10DLC compliance surface — this page is audit evidence for TextGrid
 * carrier campaign registration. Keep the STOP opt-out copy intact.
 */

import type { ContentDoc } from '../../types/content';

export const SMS_HELP_DOC: ContentDoc = {
  slug: 'sms-help',
  title: ['SMS Help'],
  subtitle: 'Everything you need to know about text messages from ORO move',
  lastUpdated: 'August 13, 2026',
  lastUpdatedPosition: 'bottom',
  sectionDividers: true,
  callout: {
    title: 'Quick answers',
    blocks: [
      {
        type: 'p',
        inlines: [
          { t: 'strong', v: 'What messages will I receive?' },
          { t: 'break' },
          'Sign-in codes, event updates, and club activity. Never marketing.',
        ],
      },
      {
        type: 'p',
        inlines: [
          { t: 'strong', v: 'How do I stop messages?' },
          { t: 'break' },
          'Reply ',
          { t: 'strong', v: 'STOP', tone: 'accent' },
          ' to any message. Reply ',
          { t: 'strong', v: 'START', tone: 'accent' },
          ' to turn them back on.',
        ],
      },
      {
        type: 'p',
        inlines: [
          { t: 'strong', v: 'Is there a cost?' },
          { t: 'break' },
          'Message and data rates from your carrier may apply.',
        ],
      },
    ],
  },
  groups: [
    [
      {
        title: ['What SMS messages does ORO move send?'],
        blocks: [
          {
            type: 'p',
            inlines: [
              { t: 'brand' },
              ' uses SMS for three things: signing in, your events, and your clubs. Never marketing.',
            ],
          },
          {
            type: 'list',
            style: 'bullet',
            items: [
              [
                { t: 'strong', v: 'Signing in' },
                ' — one-time codes to sign in, to confirm a phone number you add to your account, and to confirm deleting your account if you signed up with just a phone',
              ],
              [
                { t: 'strong', v: 'Your events' },
                " — reminders for events you've RSVP'd to, changes to those events, your own RSVP confirmations, and, if you organize an event, a note when someone RSVPs to it",
              ],
              [
                { t: 'strong', v: 'Your clubs' },
                ' — an invite when someone asks you to join a club, announcements from club leaders, and, if you lead a club, a note when someone joins',
              ],
            ],
          },
          {
            type: 'p',
            inlines: [
              'Message frequency varies with your activity. We never send marketing or promotional messages.',
            ],
          },
        ],
      },
      {
        title: ['How to opt out'],
        blocks: [
          {
            type: 'p',
            inlines: ['You can stop receiving SMS messages at any time:'],
          },
          {
            type: 'box',
            tone: 'surface-alt',
            blocks: [
              {
                type: 'p',
                emphasis: true,
                inlines: [
                  'Reply ',
                  { t: 'strong', v: 'STOP', tone: 'accent' },
                  ' to any message from ORO move',
                ],
              },
            ],
          },
          {
            type: 'p',
            inlines: [
              "You'll get a confirmation that you've been unsubscribed. To start again, reply ",
              { t: 'strong', v: 'START', tone: 'accent' },
              ' to the same number. For help, reply ',
              { t: 'strong', v: 'HELP', tone: 'accent' },
              '.',
            ],
          },
          {
            type: 'p',
            inlines: [
              { t: 'strong', v: 'One thing to know first:' },
              ' STOP stops ',
              { t: 'em', v: 'every' },
              " message, including your sign-in codes. If you sign in with your phone number and don't have an email address on your account, you won't be able to sign in until you reply START. If you'd rather keep sign-in codes and turn off event and club notifications, use your ",
              { t: 'link', v: 'notification settings', href: '/settings/notifications' },
              ' instead. Club invites are sent to a phone number rather than to an account, so they are not covered by those settings. STOP is the way to stop those.',
            ],
          },
        ],
      },
      {
        title: ['Why did I get an invite text?'],
        blocks: [
          {
            type: 'p',
            inlines: [
              'Someone invited you to their running or cycling club on ORO move, and they entered your number to send the invite.',
            ],
          },
          {
            type: 'p',
            inlines: [
              "We don't add you to anything unless you accept it. If you'd rather not hear from us, reply ",
              { t: 'strong', v: 'STOP', tone: 'accent' },
              '.',
            ],
          },
        ],
      },
      {
        title: ['Your privacy'],
        blocks: [
          {
            type: 'p',
            inlines: ['Your phone number is kept secure and private:'],
          },
          {
            type: 'list',
            style: 'bullet',
            items: [
              [
                'We ',
                { t: 'strong', v: 'never sell' },
                ' your phone number',
              ],
              [
                'We ',
                { t: 'strong', v: 'never share' },
                ' it with third parties for marketing',
              ],
              [
                "It's used ",
                { t: 'strong', v: 'only' },
                " for authentication and notifications you've opted into",
              ],
            ],
          },
          {
            type: 'p',
            inlines: [
              'For full details, see our ',
              { t: 'link', v: 'Privacy Policy', href: '/privacy' },
              '.',
            ],
          },
        ],
      },
      {
        title: ['Need more help?'],
        blocks: [
          {
            type: 'p',
            inlines: ['If you have questions about SMS messages from ORO move:'],
          },
          {
            type: 'box',
            tone: 'surface-alt',
            blocks: [
              {
                type: 'p',
                inlines: [{ t: 'link', v: 'Contact Us', href: '/contact' }],
              },
            ],
          },
          {
            type: 'p',
            inlines: ['We typically respond within 24 hours.'],
          },
        ],
      },
    ],
  ],
};
